import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check, X, Loader2, Crown, ArrowRight, ExternalLink, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantPlan } from "@/hooks/useTenantPlan";
import { toast } from "sonner";
import { FEATURE_CATALOG } from "./plan-constants";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface PlanData {
  id: string;
  name: string;
  price_monthly: number;
  enabledKeys: Set<string>;
  limits: Record<string, number>;
}

type CheckoutState = "idle" | "document_required" | "processing" | "success";

export function UpgradePlanModal({ open, onOpenChange }: Props) {
  const { currentTenant, user } = useAuth();
  const { planName } = useTenantPlan();
  const [plans, setPlans] = useState<PlanData[]>([]);
  const [loading, setLoading] = useState(false);
  const [checkoutState, setCheckoutState] = useState<CheckoutState>("idle");
  const [selectedPlan, setSelectedPlan] = useState<PlanData | null>(null);
  const [paymentLink, setPaymentLink] = useState<string>("");
  const [document, setDocument] = useState("");
  const [documentType, setDocumentType] = useState<"cpf" | "cnpj">("cpf");
  const [savingDoc, setSavingDoc] = useState(false);

  useEffect(() => {
    if (open) {
      loadPlans();
      setCheckoutState("idle");
      setPaymentLink("");
      setSelectedPlan(null);
    }
  }, [open]);

  const loadPlans = async () => {
    setLoading(true);
    const { data: plansData } = await supabase
      .from("plans")
      .select("id, name, price_monthly")
      .eq("is_active", true)
      .eq("is_public", true)
      .order("price_monthly");

    if (!plansData) { setLoading(false); return; }

    const mapped: PlanData[] = [];
    for (const p of plansData) {
      const { data: pf } = await supabase
        .from("plan_features")
        .select("feature_key, is_enabled, limit_value")
        .eq("plan_id", p.id);
      const enabledKeys = new Set(
        (pf ?? []).filter((f: any) => f.is_enabled).map((f: any) => f.feature_key)
      );
      const limits: Record<string, number> = {};
      for (const f of (pf ?? [])) {
        if ((f as any).limit_value != null) {
          limits[(f as any).feature_key] = (f as any).limit_value;
        }
      }
      mapped.push({ id: p.id, name: p.name, price_monthly: p.price_monthly ?? 0, enabledKeys, limits });
    }
    setPlans(mapped);
    setLoading(false);
  };

  const checkDocumentAndStartCheckout = async (plan: PlanData) => {
    if (!user) return;
    setSelectedPlan(plan);

    // Check if user has document
    const { data: profile } = await supabase
      .from("profiles")
      .select("cpf, cnpj")
      .eq("user_id", user.id)
      .single();

    const hasDoc = profile?.cpf || profile?.cnpj;
    if (!hasDoc) {
      setCheckoutState("document_required");
      return;
    }

    await startCheckout(plan.id);
  };

  const saveDocumentAndProceed = async () => {
    if (!user || !selectedPlan) return;

    const cleanDoc = document.replace(/\D/g, "");
    if (documentType === "cpf" && cleanDoc.length !== 11) {
      toast.error("CPF deve ter 11 dígitos");
      return;
    }
    if (documentType === "cnpj" && cleanDoc.length !== 14) {
      toast.error("CNPJ deve ter 14 dígitos");
      return;
    }

    setSavingDoc(true);
    const updateField = documentType === "cpf" ? { cpf: cleanDoc } : { cnpj: cleanDoc };
    const { error } = await supabase
      .from("profiles")
      .update(updateField)
      .eq("user_id", user.id);

    if (error) {
      toast.error("Erro ao salvar documento");
      setSavingDoc(false);
      return;
    }

    setSavingDoc(false);
    await startCheckout(selectedPlan.id);
  };

  const startCheckout = async (planId: string) => {
    setCheckoutState("processing");

    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;

      if (!token) {
        toast.error("Sessão expirada. Faça login novamente.");
        setCheckoutState("idle");
        return;
      }

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/saas-checkout`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({ plan_id: planId }),
        }
      );

      const result = await res.json();

      if (!res.ok) {
        toast.error(result.error || "Erro ao gerar pagamento");
        setCheckoutState("idle");
        return;
      }

      setPaymentLink(result.payment_link);
      setCheckoutState("success");

      // Open payment link in new tab
      if (result.payment_link) {
        window.open(result.payment_link, "_blank");
      }
    } catch (err) {
      toast.error("Erro de conexão. Tente novamente.");
      setCheckoutState("idle");
    }
  };

  const isCurrent = (name: string) =>
    planName?.toLowerCase() === name.toLowerCase();

  const isPopular = (name: string) => name === "Pro";

  // Success state
  if (checkoutState === "success") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-xl">
              🎉 Link de pagamento gerado!
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <p className="text-center text-muted-foreground text-sm">
              Após o pagamento, seu plano será ativado automaticamente em até 5 minutos.
            </p>

            <div className="flex flex-col gap-3">
              <Button
                className="w-full gap-2"
                onClick={() => paymentLink && window.open(paymentLink, "_blank")}
                disabled={!paymentLink}
              >
                <ExternalLink className="h-4 w-4" />
                Abrir link de pagamento
              </Button>

              <Button
                variant="outline"
                className="w-full gap-2"
                onClick={() => window.location.reload()}
              >
                <RefreshCw className="h-4 w-4" />
                Já paguei — verificar ativação
              </Button>
            </div>

            <p className="text-xs text-center text-muted-foreground">
              Você também pode acessar o link de pagamento em Minha Conta → Faturas
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Document required state
  if (checkoutState === "document_required") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Dados para faturamento</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Para gerar o pagamento, precisamos do seu CPF ou CNPJ.
            </p>

            <div className="flex gap-2">
              <Button
                variant={documentType === "cpf" ? "default" : "outline"}
                size="sm"
                onClick={() => { setDocumentType("cpf"); setDocument(""); }}
              >
                CPF
              </Button>
              <Button
                variant={documentType === "cnpj" ? "default" : "outline"}
                size="sm"
                onClick={() => { setDocumentType("cnpj"); setDocument(""); }}
              >
                CNPJ
              </Button>
            </div>

            <div>
              <Label>{documentType === "cpf" ? "CPF" : "CNPJ"}</Label>
              <Input
                placeholder={documentType === "cpf" ? "000.000.000-00" : "00.000.000/0000-00"}
                value={document}
                onChange={(e) => setDocument(e.target.value)}
                maxLength={documentType === "cpf" ? 14 : 18}
              />
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setCheckoutState("idle")}
              >
                Voltar
              </Button>
              <Button
                className="flex-1"
                disabled={!document || savingDoc}
                onClick={saveDocumentAndProceed}
              >
                {savingDoc && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Continuar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Processing state
  if (checkoutState === "processing") {
    return (
      <Dialog open={open} onOpenChange={() => {}}>
        <DialogContent className="max-w-sm">
          <div className="flex flex-col items-center py-8 gap-4">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground text-center">
              Gerando seu link de pagamento...
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Default — plan selection
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">Escolha o plano ideal para sua empresa</DialogTitle>
          <p className="text-center text-muted-foreground">
            Todos os planos incluem suporte e atualizações contínuas
          </p>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-3 mt-4">
            {plans.map((plan) => {
              const current = isCurrent(plan.name);
              const popular = isPopular(plan.name);
              const limits = plan.limits;

              return (
                <Card
                  key={plan.id}
                  className={`relative transition-shadow ${
                    popular
                      ? "ring-2 ring-primary shadow-lg"
                      : current
                      ? "ring-2 ring-muted-foreground/40"
                      : ""
                  }`}
                >
                  {popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-primary text-primary-foreground gap-1 px-3">
                        <Crown className="h-3 w-3" /> Mais popular
                      </Badge>
                    </div>
                  )}
                  {current && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge variant="outline" className="bg-background px-3">
                        Seu plano atual
                      </Badge>
                    </div>
                  )}

                  <CardHeader className="text-center pt-8 pb-4">
                    <h3 className="text-xl font-bold">{plan.name}</h3>
                    <div className="mt-2">
                      {plan.price_monthly > 0 ? (
                        <>
                          <span className="text-3xl font-bold">
                            R$ {plan.price_monthly.toFixed(0)}
                          </span>
                          <span className="text-muted-foreground">/mês</span>
                        </>
                      ) : (
                        <span className="text-3xl font-bold">Gratuito</span>
                      )}
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    {/* Limits */}
                    <div className="space-y-1 text-sm">
                      {Object.entries(limits).map(([key, val]) => {
                        const labelMap: Record<string, string> = {
                          max_leads: "Leads", max_deals: "Oportunidades", max_users: "Usuários",
                          max_storage_mb: "Storage (MB)", max_propostas: "Propostas/mês",
                          max_contratos: "Contratos/mês", max_whatsapp_mes: "WhatsApp/mês",
                          max_pipelines: "Pipelines",
                        };
                        return (
                          <div key={key} className="flex justify-between text-muted-foreground">
                            <span>{labelMap[key] ?? key}</span>
                            <span className="font-medium text-foreground">{(val as number).toLocaleString()}</span>
                          </div>
                        );
                      })}
                    </div>

                    {/* Features */}
                    <div className="space-y-1.5 pt-2 border-t">
                      {FEATURE_CATALOG.filter(f => !f.key.startsWith("max_")).map((f) => {
                        const enabled = plan.enabledKeys.has(f.key);
                        return (
                          <div key={f.key} className="flex items-center gap-2 text-sm">
                            {enabled ? (
                              <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                            ) : (
                              <X className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />
                            )}
                            <span className={enabled ? "" : "text-muted-foreground/50"}>
                              {f.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    <Button
                      className="w-full gap-2"
                      variant={current ? "outline" : popular ? "default" : "secondary"}
                      disabled={current}
                      onClick={() => checkDocumentAndStartCheckout(plan)}
                    >
                      {current ? "Plano Atual" : (
                        <>Iniciar upgrade <ArrowRight className="h-4 w-4" /></>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <p className="text-xs text-center text-muted-foreground mt-4">
          Pagamento seguro via PIX, boleto ou cartão de crédito. Plano ativa automaticamente após confirmação.
        </p>
      </DialogContent>
    </Dialog>
  );
}
